<template>
  <div
    v-if="showLoadingState"
    key="loadingState"
  >
    <AtomLoadingSecondary/>
  </div>
  <div
    v-else-if="!licencia"
    key="notFound"
    class="m-edition-billing-artificial-intelligence-usage"
  >
    <p class="text-align-center">
      {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.messages.notFound') }}
    </p>
  </div>
  <div
    v-else
    key="artificialIntelligenceUsageRecord"
    class="m-edition-billing-artificial-intelligence-usage"
  >
    <!-- Los avisos del core-api: con el registro apagado todo lo de abajo se ve en cero -->
    <div
      v-if="aviso"
      class="m-edition-billing-artificial-intelligence-usage-notice"
    >
      <span class="m-edition-billing-artificial-intelligence-usage-notice-icon">
        <i class="fas fa-exclamation-triangle"></i>
      </span>
      <span>
        <b>{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.warnings.title') }}</b>
        <span class="display-block">{{ aviso }}</span>
      </span>
    </div>

    <!-- Quien es, que compro y en que estado esta -->
    <div class="m-edition-billing-artificial-intelligence-usage-card margin-bottom-large">
      <div class="m-edition-billing-artificial-intelligence-usage-head">
        <div>
          <h4>{{ licencia.nombre || $t('mEditionBillingArtificialIntelligenceUsageRecord.messages.noName') }}</h4>
          <p class="card-subtitle">{{ licencia.email }}</p>
        </div>
        <div class="text-align-right">
          <span class="display-block font-weight-bold">{{ licencia.plan }}</span>
          <span class="display-block card-subtitle">
            {{ $t(`mEditionBillingArtificialIntelligenceUsageRecord.modalities.${licencia.modalidad}`) }}
          </span>
          <span :class="['font-weight-bold', claseEstado]">
            {{ $t(`mEditionBillingArtificialIntelligenceUsageRecord.states.${estado}`) }}
          </span>
        </div>
      </div>

      <ul class="m-edition-billing-artificial-intelligence-usage-facts">
        <li>
          <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.labels.purchase') }}</span>
          <span>{{ fechaLarga(licencia.fecha_compra) }}</span>
        </li>
        <li v-if="licencia.periodo">
          <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.labels.period') }}</span>
          <span>{{ fechaLarga(licencia.periodo) }}</span>
        </li>
        <li v-if="licencia.modalidad === 'mensual'">
          <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.labels.cycleDay') }}</span>
          <span>{{ licencia.dia_ciclo }}</span>
        </li>
        <li v-if="licencia.modalidad === 'mensual' && typeof licencia.usar_creditos === 'boolean'">
          <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.labels.useCredits') }}</span>
          <span>
            {{ licencia.usar_creditos
              ? $t('mEditionBillingArtificialIntelligenceUsageRecord.values.yes')
              : $t('mEditionBillingArtificialIntelligenceUsageRecord.values.no') }}
          </span>
        </li>
        <li>
          <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.labels.account') }}</span>
          <span class="font-size-xsmall">{{ licencia.cuenta_satelite }}</span>
        </li>
      </ul>

      <!-- Los dos hilos que se siguen desde aca y que ya existen en app-sales -->
      <p class="m-edition-billing-artificial-intelligence-usage-links">
        <a
          :href="urlMonitoreoUsuario"
          @click.prevent="irAMonitoreoUsuario"
        >
          <i class="fas fa-address-card margin-right-xsmall"></i>
          {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.links.user') }}
        </a>
        <a
          v-if="licencia.venta"
          :href="urlVenta"
          @click.prevent="irAVenta"
        >
          <i class="fas fa-file-invoice-dollar margin-right-xsmall"></i>
          {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.links.sale') }}
        </a>
      </p>
    </div>

    <!-- Los creditos: lo que compro, lo que le queda y lo que lleva gastado -->
    <ul class="m-edition-billing-artificial-intelligence-usage-tiles">
      <li class="m-edition-billing-artificial-intelligence-usage-tile tile-accent-credito">
        <span class="tile-icon"><i class="fas fa-coins"></i></span>
        <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.labels.credits') }}</span>
        <span class="tile-value">{{ $utils.formatCredits(licencia.creditos) }}</span>
      </li>
      <li class="m-edition-billing-artificial-intelligence-usage-tile tile-accent-ok">
        <span class="tile-icon"><i class="fas fa-battery-three-quarters"></i></span>
        <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.labels.available') }}</span>
        <span class="tile-value">{{ $utils.formatCredits(licencia.disponible) }}</span>
        <span class="tile-detail">
          {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.labels.of') }}
          {{ $utils.formatCredits(licencia.creditos) }}
        </span>
      </li>
      <li class="m-edition-billing-artificial-intelligence-usage-tile tile-accent-uso">
        <span class="tile-icon"><i class="fas fa-sync-alt"></i></span>
        <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.labels.cycleCurrent') }}</span>
        <span class="tile-value">{{ $utils.formatCredits(licencia.consumo_ciclo) }}</span>
      </li>
      <li class="m-edition-billing-artificial-intelligence-usage-tile tile-accent-uso">
        <span class="tile-icon"><i class="fas fa-chart-line"></i></span>
        <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.labels.cycleAccumulated') }}</span>
        <span class="tile-value">{{ $utils.formatCredits(licencia.consumo_acumulado) }}</span>
      </li>
    </ul>

    <!-- Lo que genero de verdad y lo que nos costo -->
    <h3 class="m-edition-billing-artificial-intelligence-usage-title">
      {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.totals.title') }}
    </h3>
    <ul class="m-edition-billing-artificial-intelligence-usage-tiles">
      <li class="m-edition-billing-artificial-intelligence-usage-tile tile-hero">
        <span class="tile-icon"><i class="fas fa-sack-dollar"></i></span>
        <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.totals.costUsd') }}</span>
        <span class="tile-value tile-value-hero">
          $ {{ formatUsd(totales.costo_usd) }}
          <span class="tile-unit">USD</span>
        </span>
        <!-- Sin registro el cero de arriba no significa "no se gasto", sino "no se anoto" -->
        <span
          v-if="!registroHabilitado"
          class="tile-detail text-color-warning"
        >
          {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.warnings.noRegistry') }}
        </span>
      </li>
      <li class="m-edition-billing-artificial-intelligence-usage-tile tile-accent-uso">
        <span class="tile-icon"><i class="fas fa-magic"></i></span>
        <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.totals.generations') }}</span>
        <span class="tile-value">{{ $utils.formatCredits(totales.generaciones) }}</span>
        <span class="tile-detail">
          {{ $utils.formatCredits(totales.exitosas) }}
          {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.totals.successful') }}
          &bull;
          {{ $utils.formatCredits(totales.fallidas) }}
          {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.totals.failed') }}
        </span>
      </li>
      <li class="m-edition-billing-artificial-intelligence-usage-tile tile-accent-ok">
        <span class="tile-icon"><i class="fas fa-layer-group"></i></span>
        <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.totals.amount') }}</span>
        <span class="tile-value">{{ $utils.formatCredits(totales.cantidad) }}</span>
      </li>
      <li class="m-edition-billing-artificial-intelligence-usage-tile tile-accent-credito">
        <span class="tile-icon"><i class="fas fa-coins"></i></span>
        <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.totals.credits') }}</span>
        <span class="tile-value">{{ $utils.formatCredits(totales.creditos) }}</span>
        <span class="tile-detail">
          {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.totals.creditsCharged') }}:
          {{ $utils.formatCredits(totales.creditos_cobrados) }}
        </span>
        <span
          v-if="!cobroHabilitado"
          class="tile-detail text-color-warning"
        >
          {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.warnings.noCharge') }}
        </span>
      </li>
    </ul>

    <p class="m-edition-billing-artificial-intelligence-usage-currency-note">
      <i class="fas fa-info-circle margin-right-xsmall"></i>
      <span>{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.warnings.orientative') }}</span>
    </p>

    <!--
      Lo que pago el cliente y lo que costo el proveedor, uno al lado del otro. Cada uno con su
      moneda y NUNCA restados: no hay tipo de cambio guardado en ningun lado.
    -->
    <div class="m-edition-billing-artificial-intelligence-usage-card margin-bottom-large margin-top-large">
      <h4>{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.income.title') }}</h4>
      <p class="card-subtitle">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.income.subtitle') }}</p>
      <div class="m-edition-billing-artificial-intelligence-usage-income">
        <div class="income-side">
          <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.income.paid') }}</span>
          <template v-if="licencia.ingreso && licencia.ingreso.pagos">
            <span class="income-amount">
              {{ licencia.ingreso.divisa_simbolo || '' }} {{ Number(licencia.ingreso.monto).toFixed(2) }}
              <span class="text-color-black-35">{{ licencia.ingreso.divisa || '' }}</span>
            </span>
            <small class="income-note">
              {{ licencia.ingreso.pagos }}
              {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.income.payments') }}
              <span v-if="licencia.ingreso.ultimo_pago">
                &bull;
                {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.income.lastPayment') }}
                {{ fechaLarga(licencia.ingreso.ultimo_pago) }}
              </span>
            </small>
          </template>
          <p
            v-else
            class="income-empty"
          >
            {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.income.empty') }}
          </p>
        </div>
        <div class="income-side income-side-costo">
          <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.income.cost') }}</span>
          <span class="income-amount">
            $ {{ formatUsd(totales.costo_usd) }}
            <span class="text-color-black-35">USD</span>
          </span>
        </div>
      </div>
      <p
        v-if="avisoDivisas"
        class="m-edition-billing-artificial-intelligence-usage-currency-note"
      >
        <i class="fas fa-info-circle margin-right-xsmall"></i>
        <span>{{ avisoDivisas }}</span>
      </p>
    </div>

    <!-- Graficos de la licencia -->
    <div class="m-edition-billing-artificial-intelligence-usage-charts">
      <div class="m-edition-billing-artificial-intelligence-usage-card">
        <h4>{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.charts.byAppTitle') }}</h4>
        <p class="card-subtitle">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.charts.byAppSubtitle') }}</p>
        <MoleculeChartBars
          :color="colorSerie"
          :empty-text="$t('mEditionBillingArtificialIntelligenceUsageRecord.charts.empty')"
          :series="serieApps"
        />
      </div>

      <div class="m-edition-billing-artificial-intelligence-usage-card">
        <h4>{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.charts.byTypeTitle') }}</h4>
        <p class="card-subtitle">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.charts.byTypeSubtitle') }}</p>
        <MoleculeChartStack
          :empty-text="$t('mEditionBillingArtificialIntelligenceUsageRecord.charts.empty')"
          :series="serieTipos"
        />
      </div>

      <div class="m-edition-billing-artificial-intelligence-usage-card card-wide">
        <h4>{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.charts.byCycleTitle') }}</h4>
        <p class="card-subtitle">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.charts.byCycleSubtitle') }}</p>
        <MoleculeChartColumns
          :color="colorSerie"
          :empty-text="$t('mEditionBillingArtificialIntelligenceUsageRecord.history.empty')"
          :formatter="formatCreditos"
          :series="serieCiclos"
          :threshold="licencia.creditos || 0"
          :threshold-text="$t('mEditionBillingArtificialIntelligenceUsageRecord.values.planCredits')"
        />
      </div>
    </div>

    <!-- Totales por tipo, escritos: el reparto de arriba no puede ser la unica forma de leerlos -->
    <h3 class="m-edition-billing-artificial-intelligence-usage-title">
      {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.charts.byTypeTitle') }}
    </h3>
    <div class="m-table-primary">
      <table>
        <tr v-show="porTipo.length">
          <th class="text-align-left">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.typeTable.thType') }}</th>
          <th class="text-align-right">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.typeTable.thGenerations') }}</th>
          <th class="text-align-right">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.typeTable.thAmount') }}</th>
          <th class="text-align-right">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.typeTable.thCostUsd') }}</th>
          <th class="text-align-right">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.typeTable.thCredits') }}</th>
        </tr>
        <tr
          v-for="tipo in porTipo"
          :key="'tipo-' + tipo.tipo"
          class="row-data"
        >
          <td class="text-align-left">
            {{ $t(`mEditionBillingArtificialIntelligenceUsageRecord.types.${tipo.tipo}`) }}
          </td>
          <td class="text-align-right text-white-space-nowrap">
            {{ $utils.formatCredits(tipo.generaciones) }}
            <small
              v-if="tipo.fallidas"
              class="text-color-danger"
            >
              ({{ $utils.formatCredits(tipo.fallidas) }}
              {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.totals.failed') }})
            </small>
          </td>
          <td class="text-align-right text-white-space-nowrap">{{ $utils.formatCredits(tipo.cantidad) }}</td>
          <td class="text-align-right text-white-space-nowrap">
            $ {{ formatUsd(tipo.costo_usd) }}
            <span class="text-color-black-35">USD</span>
          </td>
          <td class="text-align-right text-white-space-nowrap">
            {{ $utils.formatCredits(tipo.creditos) }}
            <small class="text-color-black-35">
              ({{ $utils.formatCredits(tipo.creditos_cobrados) }})
            </small>
          </td>
        </tr>
      </table>
      <p
        v-show="!porTipo.length"
        class="text-align-center margin-bottom-zero"
      >
        {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.charts.empty') }}
      </p>
    </div>

    <!-- Historial ciclo a ciclo -->
    <h3 class="m-edition-billing-artificial-intelligence-usage-title">
      {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.history.title') }}
      <small>{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.history.subtitle') }}</small>
    </h3>
    <div class="m-table-primary">
      <table>
        <tr v-show="historico.length">
          <th class="text-align-left">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.historyTable.thPeriod') }}</th>
          <th
            v-for="app in appsIa"
            :key="'th-app-' + app"
            class="text-align-right"
          >
            {{ $t(`mEditionBillingArtificialIntelligenceUsageRecord.apps.${app}`) }}
          </th>
          <th class="text-align-right">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.historyTable.thTotal') }}</th>
          <th class="text-align-right">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.historyTable.thAccumulated') }}</th>
          <th class="text-align-right">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.historyTable.thCredits') }}</th>
          <th class="text-align-center">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.historyTable.thCurrent') }}</th>
        </tr>
        <tr
          v-for="ciclo in historico"
          :key="'ciclo-' + ciclo.periodo"
          class="row-data"
        >
          <td class="text-align-left text-white-space-nowrap">{{ fechaLarga(ciclo.periodo) }}</td>
          <td
            v-for="app in appsIa"
            :key="'td-app-' + app"
            class="text-align-right text-white-space-nowrap"
          >
            {{ $utils.formatCredits(ciclo['consumo_' + app]) }}
          </td>
          <td class="text-align-right text-white-space-nowrap font-weight-bold">
            {{ $utils.formatCredits(ciclo.consumo_total) }}
          </td>
          <td class="text-align-right text-white-space-nowrap">{{ $utils.formatCredits(ciclo.consumo_acumulado) }}</td>
          <td class="text-align-right text-white-space-nowrap">{{ $utils.formatCredits(ciclo.creditos) }}</td>
          <td class="text-align-center">
            {{ ciclo.vigente
              ? $t('mEditionBillingArtificialIntelligenceUsageRecord.values.yes')
              : $t('mEditionBillingArtificialIntelligenceUsageRecord.values.no') }}
          </td>
        </tr>
      </table>
      <p
        v-show="!historico.length"
        class="text-align-center margin-bottom-zero"
      >
        {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.history.empty') }}
      </p>
    </div>

    <!-- El detalle fino: una fila por llamada a la IA -->
    <h3 class="m-edition-billing-artificial-intelligence-usage-title">
      {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.detail.title') }}
      <small>{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.detail.subtitle') }}</small>
    </h3>

    <div class="align-items-baseline display-flex-row">
      <form
        autocomplete="off"
        class="m-edition-billing-artificial-intelligence-usage-range"
        name="generaciones-filters-form"
        @submit.prevent="searchSimpleFilter"
      >
        <AtomSelectPrimary
          v-model="simpleFilter.app"
          :disabled="pagination.showLoadingState"
          :elements="appElements"
          keyField="id"
          name="app"
          textField="name"
          wrapperClass="a-input-wrapper-row"
          @change="searchSimpleFilter"
        />
        <AtomSelectPrimary
          v-model="simpleFilter.tipo"
          :disabled="pagination.showLoadingState"
          :elements="tipoElements"
          keyField="id"
          name="tipo"
          textField="name"
          wrapperClass="a-input-wrapper-row"
          @change="searchSimpleFilter"
        />
        <AtomSelectPrimary
          v-model="simpleFilter.exito"
          :disabled="pagination.showLoadingState"
          :elements="exitoElements"
          keyField="id"
          name="exito"
          textField="name"
          wrapperClass="a-input-wrapper-row"
          @change="searchSimpleFilter"
        />
        <AtomInputPrimary
          v-model="simpleFilter.desde"
          :disabled="pagination.showLoadingState"
          :label="$t('mEditionBillingArtificialIntelligenceUsageRecord.filters.from')"
          name="desde"
          type="date"
          wrapperClass="a-input-wrapper-column"
          @change="searchSimpleFilter"
        />
        <AtomInputPrimary
          v-model="simpleFilter.hasta"
          :disabled="pagination.showLoadingState"
          :label="$t('mEditionBillingArtificialIntelligenceUsageRecord.filters.to')"
          name="hasta"
          type="date"
          wrapperClass="a-input-wrapper-column"
          @change="searchSimpleFilter"
        />
      </form>
    </div>

    <div class="m-table-primary">
      <table>
        <tr v-show="generaciones.length">
          <th class="text-align-left">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.table.thDate') }}</th>
          <th class="text-align-left">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.table.thType') }}</th>
          <th class="text-align-left">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.table.thModel') }}</th>
          <th class="text-align-right">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.table.thAmount') }}</th>
          <th class="text-align-right">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.table.thTokens') }}</th>
          <th class="text-align-right">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.table.thCostUsd') }}</th>
          <th class="text-align-right">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.table.thCredits') }}</th>
          <th class="text-align-right">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.table.thDuration') }}</th>
          <th class="text-align-center">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.table.thResult') }}</th>
        </tr>
        <template v-for="generacion in generaciones">
        <tr
          :key="generacion.id"
          class="row-data"
        >
          <td class="text-align-left text-white-space-nowrap">
            <span class="display-flex-column">
              <span>{{ fechaHora(generacion.fecha) }}</span>
              <small class="font-weight-regular text-color-black-35">{{ appNombre(generacion.app) }}</small>
            </span>
          </td>
          <td class="text-align-left">
            <span class="display-flex-column">
              <span>{{ $t(`mEditionBillingArtificialIntelligenceUsageRecord.types.${generacion.tipo}`) }}</span>
              <!--
                El contexto es material del cliente: va plegado, fuera de la tabla de datos, y solo
                cuando el core-api lo manda (contexto_habilitado)
              -->
              <a
                v-if="contextoHabilitado && (generacion.contexto || generacion.resultado)"
                href="#"
                class="font-size-xsmall"
                @click.prevent="toggleContexto(generacion.id)"
              >
                {{ contextoAbierto[generacion.id]
                  ? $t('mEditionBillingArtificialIntelligenceUsageRecord.context.hide')
                  : $t('mEditionBillingArtificialIntelligenceUsageRecord.context.show') }}
              </a>
            </span>
          </td>
          <td class="text-align-left">
            <span class="display-flex-column">
              <span class="ellipsis">{{ generacion.modelo || $t('mEditionBillingArtificialIntelligenceUsageRecord.values.none') }}</span>
              <small class="font-weight-regular text-color-black-35">
                {{ generacion.proveedor || '' }}
                <span v-if="generacion.calidad">&bull; {{ generacion.calidad }}</span>
              </small>
            </span>
          </td>
          <td class="text-align-right text-white-space-nowrap">
            {{ $utils.formatCredits(generacion.cantidad) }}
            <small
              v-if="generacion.cantidad_pedida !== null && generacion.cantidad_pedida !== generacion.cantidad"
              class="text-color-black-35"
            >
              ({{ $utils.formatCredits(generacion.cantidad_pedida) }}
              {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.values.asked') }})
            </small>
          </td>
          <td class="text-align-right text-white-space-nowrap">
            <span v-if="generacion.tokens_entrada !== null || generacion.tokens_salida !== null">
              {{ $utils.formatCredits(generacion.tokens_entrada) }}
              {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.values.in') }}
              &bull;
              {{ $utils.formatCredits(generacion.tokens_salida) }}
              {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.values.out') }}
            </span>
            <span
              v-else
              class="text-color-black-35"
            >
              &mdash;
            </span>
          </td>
          <td class="text-align-right text-white-space-nowrap">$ {{ formatUsd(generacion.costo_usd) }}</td>
          <td class="text-align-right text-white-space-nowrap">
            {{ $utils.formatCredits(generacion.creditos) }}
            <small class="text-color-black-35">
              ({{ $utils.formatCredits(generacion.creditos_cobrados) }})
            </small>
          </td>
          <td class="text-align-right text-white-space-nowrap">{{ duracion(generacion.duracion_ms) }}</td>
          <td class="text-align-center">
            <span
              v-if="generacion.exito"
              class="font-weight-bold text-color-success"
            >
              {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.values.ok') }}
            </span>
            <span
              v-else
              class="display-flex-column"
            >
              <span class="font-weight-bold text-color-danger">
                {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.values.failed') }}
              </span>
              <small
                v-if="generacion.etapa"
                class="font-weight-regular"
                :title="generacion.error || ''"
              >
                {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.values.stage') }}: {{ generacion.etapa }}
              </small>
            </span>
          </td>
        </tr>
        <tr
          v-if="contextoHabilitado && contextoAbierto[generacion.id]"
          :key="generacion.id + '-contexto'"
        >
          <td
            colspan="9"
            class="m-edition-billing-artificial-intelligence-usage-context-cell"
          >
            <p class="context-warning">
              <i class="fas fa-lock margin-right-xsmall"></i>
              <span>{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.context.note') }}</span>
            </p>
            <div class="context-blocks">
              <div v-if="generacion.contexto">
                <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.context.written') }}</span>
                <pre class="context-text">{{ generacion.contexto }}</pre>
              </div>
              <div v-if="generacion.resultado">
                <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsageRecord.context.generated') }}</span>
                <pre class="context-text">{{ generacion.resultado }}</pre>
              </div>
            </div>
          </td>
        </tr>
        </template>
      </table>
      <p
        v-show="!generaciones.length"
        class="text-align-center margin-bottom-zero"
      >
        {{ $t('mEditionBillingArtificialIntelligenceUsageRecord.detail.empty') }}
      </p>
      <MoleculePaginationPrimary
        :pagination="pagination"
        @setPaginationCurrentPage="setPaginationCurrentPage"
      />
    </div>
  </div>
</template>

<script>
import configApp from '@/config'
import mEditionMenu from '@/plugins/languages/es/molecules/m-edition-section-navigation-menu'

/*
 | La ficha de UNA licencia de IA vendida, dentro del monitoreo interno de app-sales.
 |
 | Dos llamadas: la ficha (/monitoreo/ventas/{uuid}) con los totales, el consumo por app, los ciclos
 | cerrados y el reparto por tipo, y el detalle (/monitoreo/ventas/{uuid}/generaciones) paginado, que
 | es el unico bloque con filtros propios.
 |
 | El contexto que escribio el cliente y lo que genero la IA solo existen cuando el core-api los
 | manda (contexto_habilitado). Es material de los clientes del dueño, asi que va plegado, fuera de
 | la tabla de datos y con el aviso de que no se copia fuera de aca.
 */

// Las apps que consumen IA, en el orden en que se muestran (las mismas claves que el core-api)
const APPS_IA = ['juedroop', 'evaluaciones', 'academy', 'classroom']

// La misma paleta validada que usa el listado: el reparto por tipo nunca depende solo del color
const PALETA_TIPOS = ['#2a78d6', '#eb6834', '#1baf7a']

const COLOR_SERIE = '#2a78d6'

const TIPOS_IA = ['preguntas', 'portada', 'imagen_pregunta']

const TOTALES_VACIOS = {
  cantidad: 0,
  costo_usd: 0,
  creditos: 0,
  creditos_cobrados: 0,
  exitosas: 0,
  fallidas: 0,
  generaciones: 0
}

export default {
  name: 'MoleculeEditionBillingArtificialIntelligenceUsageRecord',

  components: {
    MoleculeChartBars: () => import('@/components/molecules/MoleculeChartBars'),
    MoleculeChartColumns: () => import('@/components/molecules/MoleculeChartColumns'),
    MoleculeChartStack: () => import('@/components/molecules/MoleculeChartStack'),
    MoleculePaginationPrimary: () => import('@/components/molecules/MoleculePaginationPrimary')
  },

  data () {
    return {
      appElements: [{ id: '', name: this.$t('mEditionBillingArtificialIntelligenceUsageRecord.filters.appAll') }]
        .concat(APPS_IA.map(app => ({
          id: app,
          name: this.$t(`mEditionBillingArtificialIntelligenceUsageRecord.apps.${app}`)
        }))),

      appsIa: APPS_IA,

      aviso: '',

      avisoDivisas: '',

      // Los dos interruptores del core-api. Arrancan encendidos para no gritar antes de saber
      cobroHabilitado: true,

      colorSerie: COLOR_SERIE,

      consumoPorApp: {},

      contextoAbierto: {},

      contextoHabilitado: false,

      exitoElements: [
        { id: '', name: this.$t('mEditionBillingArtificialIntelligenceUsageRecord.filters.resultAll') },
        { id: 1, name: this.$t('mEditionBillingArtificialIntelligenceUsageRecord.filters.resultOk') },
        { id: 0, name: this.$t('mEditionBillingArtificialIntelligenceUsageRecord.filters.resultFailed') }
      ],

      filter: {
        app: '',
        desde: '',
        exito: '',
        hasta: '',
        limite: 25,
        pagina: 1,
        tipo: ''
      },

      generaciones: [],

      historico: [],

      licencia: null,

      licenciaId: '',

      pagination: {
        currentPage: 1,
        currentQuantity: 0,
        limit: 25,
        showLoadingState: false,
        totalPages: 0,
        totalQuantity: 0
      },

      porTipo: [],

      registroHabilitado: true,

      showLoadingState: true,

      simpleFilter: {
        app: '',
        desde: '',
        exito: '',
        hasta: '',
        tipo: ''
      },

      tipoElements: [{ id: '', name: this.$t('mEditionBillingArtificialIntelligenceUsageRecord.filters.typeAll') }]
        .concat(TIPOS_IA.map(tipo => ({
          id: tipo,
          name: this.$t(`mEditionBillingArtificialIntelligenceUsageRecord.types.${tipo}`)
        }))),

      totales: Object.assign({}, TOTALES_VACIOS)
    }
  },

  computed: {
    claseEstado () {
      const clases = {
        activa: 'text-color-success',
        agotada: 'text-color-warning',
        eliminada: 'text-color-danger',
        suspendida: 'text-color-danger'
      }

      return clases[this.estado]
    },

    // "Agotada" no es un estado guardado: la licencia sigue vigente pero sin disponible
    estado () {
      if (!this.licencia) {
        return 'activa'
      }

      if (this.licencia.eliminada) {
        return 'eliminada'
      }

      if (!this.licencia.vigente) {
        return 'suspendida'
      }

      return this.licencia.agotada ? 'agotada' : 'activa'
    },

    serieApps () {
      return APPS_IA
        .map(app => ({
          key: app,
          label: this.appNombre(app),
          text: this.$utils.formatCredits(this.consumoPorApp[app]),
          value: Number(this.consumoPorApp[app]) || 0
        }))
        .sort((a, b) => b.value - a.value)
    },

    // Del ciclo mas viejo al mas nuevo: el grafico se lee de izquierda a derecha en el tiempo
    serieCiclos () {
      return [...this.historico]
        .sort((a, b) => `${a.periodo}`.localeCompare(`${b.periodo}`))
        .map(ciclo => ({
          key: ciclo.periodo,
          label: this.fechaCorta(ciclo.periodo),
          tooltip: `${this.fechaLarga(ciclo.periodo)} · ${this.$utils.formatCredits(ciclo.consumo_total)}`,
          value: Number(ciclo.consumo_total) || 0
        }))
    },

    serieTipos () {
      return this.porTipo.map((tipo, index) => ({
        color: PALETA_TIPOS[index % PALETA_TIPOS.length],
        key: tipo.tipo,
        label: this.$t(`mEditionBillingArtificialIntelligenceUsageRecord.types.${tipo.tipo}`),
        text: `$ ${this.formatUsd(tipo.costo_usd)}`,
        value: Number(tipo.costo_usd) || 0
      }))
    },

    urlMonitoreoUsuario () {
      const menu = mEditionMenu.mEditionMenu.billing.usersMonitoring
      const email = this.licencia ? this.licencia.email : ''

      return `/${this.$t('rPaths.billing')}/${menu.subject}?email=${encodeURIComponent(email)}`
    },

    urlVenta () {
      const menu = mEditionMenu.mEditionMenu.billing.academySales
      const venta = this.licencia ? this.licencia.venta : ''

      return `/${this.$t('rPaths.billing')}/${menu.subject}/${venta}/${menu.children.record.subedition}`
    }
  },

  created () {
    // Con el flag apagado el item no esta en el menu; la URL directa tampoco muestra la pantalla
    if (configApp.droopInteligenciaArtificialHabilitado !== true) {
      this.$router.replace(`/${this.$t('rPaths.billing')}/${this.$t('mEditionMenu.billing.satellites.subject')}`)

      return
    }

    this.licenciaId = this.$route.params.subid
    this.readLicencia()
    this.readGeneraciones()
  },

  methods: {
    appNombre (app) {
      return app
        ? this.$t(`mEditionBillingArtificialIntelligenceUsageRecord.apps.${app}`)
        : this.$t('mEditionBillingArtificialIntelligenceUsageRecord.values.none')
    },

    duracion (milisegundos) {
      const numero = Number(milisegundos) || 0

      return numero < 1000 ? `${numero} ms` : `${(numero / 1000).toFixed(1)} s`
    },

    fechaCorta (fecha) {
      return fecha ? this.$moment(fecha, 'YYYY-MM-DD').format('D MMM YY') : ''
    },

    fechaHora (fecha) {
      return fecha ? this.$moment(fecha, 'YYYY-MM-DD HH:mm:ss').format('D MMM YYYY HH:mm') : ''
    },

    fechaLarga (fecha) {
      if (!fecha) {
        return ''
      }

      const formato = `${fecha}`.length > 10 ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'

      return this.$moment(fecha, formato).format('D MMM YYYY')
    },

    formatCreditos (valor) {
      return this.$utils.formatCredits(valor)
    },

    /*
     | Los importes se escriben con dos decimales, como en el resto de app-sales. Por debajo de un
     | centavo se escriben con seis: una generacion suelta cuesta fracciones de centavo y con dos
     | decimales se veria "0.00", que no es lo mismo que gratis.
     */
    formatUsd (valor) {
      const numero = Number(valor) || 0

      return numero !== 0 && Math.abs(numero) < 0.01 ? numero.toFixed(6) : numero.toFixed(2)
    },

    irAMonitoreoUsuario () {
      this.$router.push(this.urlMonitoreoUsuario)
    },

    irAVenta () {
      this.$router.push(this.urlVenta)
    },

    readGeneraciones () {
      if (this.pagination.showLoadingState) {
        return
      }

      this.pagination.showLoadingState = true

      this.$services.read('sateliteIaMonitoreo', 'readMonitoreoIaGeneraciones', {}, [this.licenciaId], this.filter)
        .then(response => {
          this.generaciones = response.data.data || []
          this.contextoHabilitado = response.data.contexto_habilitado === true
          this.aviso = response.data.aviso || this.aviso
          this.pagination.currentQuantity = this.generaciones.length
          this.pagination.limit = response.data.limite || this.filter.limite
          this.pagination.totalPages = response.data.paginas || 0
          this.pagination.totalQuantity = response.data.total || 0
          this.pagination.showLoadingState = false
        })
        .catch(error => {
          console.log(error)
          this.generaciones = []
          this.pagination.currentQuantity = 0
          this.pagination.totalPages = 0
          this.pagination.totalQuantity = 0
          this.pagination.showLoadingState = false
          this.$alerts.pushAlert('mEditionBillingArtificialIntelligenceUsageRecord', 'readGeneraciones', 'error', 'a-alert-danger')
        })
    },

    readLicencia () {
      this.$services.read('sateliteIaMonitoreo', 'readMonitoreoIaVenta', {}, [this.licenciaId])
        .then(response => {
          this.licencia = response.data.licencia || null
          this.consumoPorApp = response.data.consumo_por_app || {}
          this.historico = response.data.historico || []
          this.porTipo = response.data.por_tipo || []
          this.totales = Object.assign({}, TOTALES_VACIOS, response.data.totales || {})
          this.aviso = response.data.aviso || ''
          this.avisoDivisas = response.data.aviso_divisas || ''
          this.cobroHabilitado = response.data.cobro_habilitado !== false
          this.contextoHabilitado = response.data.contexto_habilitado === true
          this.registroHabilitado = response.data.registro_habilitado !== false
          this.showLoadingState = false
        })
        .catch(error => {
          console.log(error)
          this.licencia = null
          this.showLoadingState = false
          this.$alerts.pushAlert('mEditionBillingArtificialIntelligenceUsageRecord', 'readLicencia', 'error', 'a-alert-danger')
        })
    },

    searchSimpleFilter () {
      this.filter.app = this.simpleFilter.app
      this.filter.desde = this.simpleFilter.desde
      this.filter.exito = this.simpleFilter.exito
      this.filter.hasta = this.simpleFilter.hasta
      this.filter.tipo = this.simpleFilter.tipo
      this.setPaginationCurrentPage(1)
    },

    setPaginationCurrentPage (page) {
      this.filter.pagina = page
      this.pagination.currentPage = page
      this.contextoAbierto = {}
      this.readGeneraciones()
    },

    // Vue 2 no ve las claves nuevas de un objeto: se reemplaza entero
    toggleContexto (id) {
      const abiertos = Object.assign({}, this.contextoAbierto)

      abiertos[id] = !abiertos[id]
      this.contextoAbierto = abiertos
    }
  }
}
</script>
